import { jsx as _jsx } from "react/jsx-runtime";
import { Button, Result } from "antd";
import { observer } from "mobx-react";
import { useStores } from "../../../store/stores.js";
import { __ } from "../../../utils/i18n.js";
/**
 * Full-page error view for unrecoverable checkout failures. Two distinct
 * phases pick different copy: bootstrap (product/pay-link resolution and
 * pre-submit timeouts) and mid-flow (errors after the buyer already
 * submitted their identification, e.g. an unhandled `applyPayment` code).
 */ const FatalError = observer(({ error })=>{
    const { checkoutV2Store } = useStores();
    const { isBackendDown } = error;
    const isProductNotFound = error.hasErrorCode("ProductPlanNotFound");
    const isPayLinkInvalid = error.hasErrorCode([
        "PayLinkMalformed",
        "PayLinkNotFound"
    ]);
    const isTimeout = error.hasErrorCode("CheckoutBootstrapTimeout");
    // Permanent conditions (product gone, offer expired) have no retry value — send the
    // user back to the homepage. Transient conditions (timeout, service down, generic)
    // usually resolve on their own, so offer a page reload instead.
    const isPermanentFailure = isProductNotFound || isPayLinkInvalid;
    // Anything triggered after the buyer already submitted their details
    // (e.g. an unhandled `applyPayment` error mid-payment) — the "preparing
    // your checkout" framing no longer fits because we are no longer in the
    // bootstrap phase.
    const isMidFlow = !!checkoutV2Store.transaction?.identification;
    let title;
    let subTitle;
    if (isProductNotFound) {
        title = __("This product is no longer available.");
        subTitle = __("Browse our current plans on the website, or contact us if you need help choosing an alternative.");
    } else if (isPayLinkInvalid) {
        title = __("This offer has expired.");
        subTitle = __("Our checkout links are active for a limited time. Reach out to your contact and they'll be happy to set up a fresh one for you.");
    } else if (isTimeout) {
        title = __("Our checkout is taking longer than usual.");
        subTitle = __("We could not finish preparing your checkout in time. Please give it another try in a moment!");
    } else if (isBackendDown) {
        title = __("We can't reach our checkout service right now.");
        subTitle = __("This is on us and we're already looking into it. Please try again shortly!");
    } else if (isMidFlow) {
        title = __("Something went wrong while completing your order.");
        subTitle = error.message;
    } else {
        title = __("Something went wrong while preparing your checkout.");
        subTitle = error.message;
    }
    return /*#__PURE__*/ _jsx(Result, {
        status: "warning",
        title: title,
        subTitle: subTitle,
        extra: isPermanentFailure ? /*#__PURE__*/ _jsx(Button, {
            type: "primary",
            size: "large",
            href: "/",
            children: __("Back to homepage")
        }) : /*#__PURE__*/ _jsx(Button, {
            type: "primary",
            size: "large",
            onClick: ()=>window.location.reload(),
            children: __("Reload page")
        })
    });
});
export { FatalError };
